/**
 * Results Cache Store
 *
 * Caches search results and scroll position per threadId for instant back navigation
 * from PDP. Uses sessionStorage for per-tab persistence.
 *
 * Pattern follows search-metadata-store.ts
 */

import type { SortOrder } from "@/components/results/sort-utils";
import type { FiltersState } from "@/hooks/useResultsFilters";
import type { Product } from "@/lib/product-utils";
import type { ShopperTouchedSnapshot } from "@/lib/results-empty-state";
import type { ResultsSearchState } from "@/lib/shopper-search";

import type { SimilarProductsRequest } from "../../openapi/shop_agent/client/types.gen";

export interface ResultsCacheEntry {
  threadId: string;
  products: Product[];
  sortOrder: SortOrder;
  filters: FiltersState;
  scrollY: number;
  savedAt: number;
  searchTextQuery: string | null;
  searchBreadcrumbPath: string[] | null;
  /** What the shopper touched in the sidebar; restored with `filters`. Absent in older entries. */
  shopperTouched?: ShopperTouchedSnapshot | null;
  /** The shopper-route search the results came from, so edits stay `/v1` requests. */
  search: ResultsSearchState | null;
  /** The "More like this" request when the results are one. */
  moreLikeThis: SimilarProductsRequest | null;
}

/**
 * Whether a stored entry has this shape. Entries written before the page's
 * state became a `ResultsSearchState` (OCT-4640) lack `search` and are
 * dropped: the page searches again rather than restoring a state it cannot
 * edit.
 */
function isCurrentEntry(entry: ResultsCacheEntry): boolean {
  return "search" in entry && "moreLikeThis" in entry;
}

const STORAGE_KEY = "resultsCache";
const TTL_MS = 30 * 60 * 1000; // 30 minutes
const MAX_ENTRIES = 10;

class ResultsCacheStore {
  private cache: Map<string, ResultsCacheEntry> = new Map();

  private shouldLogStorageWarnings(): boolean {
    // Unit tests intentionally exercise corrupted/quota scenarios; avoid noisy test output.
    return process.env.NODE_ENV !== "test";
  }

  constructor() {
    if (typeof window !== "undefined") {
      this.loadFromSessionStorage();
      // Clean up expired entries periodically
      setInterval(() => this.cleanup(), 5 * 60 * 1000); // Every 5 minutes
    }
  }

  /**
   * Save results cache for a thread
   */
  save(entry: ResultsCacheEntry): void {
    const entryWithTimestamp: ResultsCacheEntry = {
      ...entry,
      savedAt: Date.now(),
    };

    this.cache.set(entry.threadId, entryWithTimestamp);

    // Enforce max entries (LRU eviction)
    if (this.cache.size > MAX_ENTRIES) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }

    this.saveToSessionStorage();
  }

  /**
   * Get cached results for a thread
   * Returns null if cache miss or expired
   */
  get(threadId: string): ResultsCacheEntry | null {
    const entry = this.cache.get(threadId);

    if (!entry) {
      return null;
    }

    // Check TTL
    if (Date.now() - entry.savedAt > TTL_MS) {
      this.cache.delete(threadId);
      this.saveToSessionStorage();
      return null;
    }

    // Move to end for LRU ordering (delete and re-insert)
    this.cache.delete(threadId);
    this.cache.set(threadId, entry);
    this.saveToSessionStorage();

    return entry;
  }

  /**
   * Invalidate cache for a specific thread
   */
  invalidate(threadId: string): void {
    if (this.cache.has(threadId)) {
      this.cache.delete(threadId);
      this.saveToSessionStorage();
    }
  }

  /**
   * Check if valid cache exists for a thread (without returning the data)
   */
  has(threadId: string): boolean {
    return this.get(threadId) !== null;
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const expired: string[] = [];

    this.cache.forEach((entry, threadId) => {
      if (now - entry.savedAt > TTL_MS) {
        expired.push(threadId);
      }
    });

    expired.forEach((threadId) => {
      this.cache.delete(threadId);
    });

    if (expired.length > 0) {
      this.saveToSessionStorage();
    }
  }

  /**
   * Save to sessionStorage
   */
  private saveToSessionStorage(): void {
    if (typeof window === "undefined") return;

    try {
      const data = Array.from(this.cache.entries());
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      // Storage quota exceeded or other error - fail gracefully
      if (this.shouldLogStorageWarnings()) {
        console.warn("Failed to save results cache to sessionStorage:", error);
      }
    }
  }

  /**
   * Load from sessionStorage on initialization
   */
  private loadFromSessionStorage(): void {
    if (typeof window === "undefined") return;

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (data) {
        const entries: [string, ResultsCacheEntry][] = JSON.parse(data);
        this.cache = new Map(entries.filter(([, entry]) => isCurrentEntry(entry)));
        // Run cleanup immediately to remove any expired entries
        this.cleanup();
      }
    } catch (error) {
      // Corrupted storage (or access errors) should not break the app.
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }

      if (this.shouldLogStorageWarnings()) {
        console.warn("Failed to load results cache from sessionStorage:", error);
      }
    }
  }

  /**
   * Clear all cache (useful for testing)
   */
  clear(): void {
    this.cache.clear();
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }
}

// Singleton instance
export const resultsCacheStore = new ResultsCacheStore();
